import { useState, useRef } from "react";
import axios from "axios";
import { makeRequest } from "../makeRequest";
import countries from "i18n-iso-countries";
import { useTranslation } from "react-i18next";

// Rate lookups are frequently triggered more than once for the same
// destination (the debounced checkout effects, address edits, retry clicks).
// Share one in-flight promise per identical key and briefly memoise the
// result so repeated selections are instant instead of hammering the API.
const RATE_CACHE_TTL_MS = 5 * 60 * 1000;
const inFlightRates = new Map();
const rateCache = new Map();

const getRateCacheKey = (weight, countryIso2Code, cityName, postalCode) =>
  [
    weight,
    countryIso2Code,
    (cityName || "").trim().toLowerCase(),
    (postalCode || "").trim(),
  ].join("|");

const useDHLShipping = () => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tries, setTries] = useState(0);

  // Reference-counted loading so a fast request finishing cannot clear the
  // flag while a slower one is still running.
  const pendingRequests = useRef(0);
  const beginLoading = () => {
    pendingRequests.current += 1;
    setLoading(true);
  };
  const endLoading = () => {
    pendingRequests.current = Math.max(0, pendingRequests.current - 1);
    setLoading(pendingRequests.current > 0);
  };

  const retry = () => {
    setTries((prev) => prev + 1);
  };

  const getDHLSettings = async () => {
    setLoading(true);
    setError("");
    let result = [];

    const Params = new URLSearchParams({
      populate: "*",
      "pagination[limit]": -1,
    });

    // get countries
    const countriesBaseUrl = "/settings";
    const countriesUrl = `${countriesBaseUrl}?${Params.toString()}`;

    try {
      result = await makeRequest.get(countriesUrl);
    } catch (err) {
      setError(t("errors.unknown"));
    }
    endLoading();
    return result?.data?.data;
  };

  const getProductPrice = async (product, exchange) => {
    let productPrice = 0;
    for (const totalPrice of product?.totalPrice) {
      if (totalPrice?.priceCurrency == "AED") {
        productPrice = totalPrice?.price;
        break;
      }
    }

    if (productPrice == 0) {
      let price = product?.totalPrice[0]?.price;
      let currency = product?.totalPrice[0]?.priceCurrency;

      for (const rate of exchange) {
        if (rate?.baseCurrency == currency && rate?.currency == "AED") {
          price = price / rate?.currentExchangeRate;
        }
      }

      productPrice = price;
    }

    return Number(productPrice.toFixed(2));
  };

  const getRates = async (
    weight,
    countryName,
    cityName,
    postalCode,
    settings
  ) => {
    const packageWeigth = settings?.find(
      (setting) => setting?.attributes?.SettingName == "DHL Max Package Weight"
    )?.attributes?.SettingValue;
    const totalWeight = parseFloat(weight);

    const length = settings?.find(
      (setting) => setting?.attributes?.SettingName == "DHL Length"
    )?.attributes?.SettingValue;

    const width = settings?.find(
      (setting) => setting?.attributes?.SettingName == "DHL Width"
    )?.attributes?.SettingValue;

    const height = settings?.find(
      (setting) => setting?.attributes?.SettingName == "DHL Height"
    )?.attributes?.SettingValue;

    const today = new Date();
    let plannedShippingDate = new Date(today);
    plannedShippingDate.setDate(today.getDate() + 1);

    if (plannedShippingDate.getDay() === 0) {
      plannedShippingDate.setDate(plannedShippingDate.getDate() + 1);
    }

    // Format the date as YYYY-MM-DD
    const formattedPlannedShippingDate = `${plannedShippingDate.getFullYear()}-${String(
      plannedShippingDate.getMonth() + 1
    ).padStart(2, "0")}-${String(plannedShippingDate.getDate()).padStart(
      2,
      "0"
    )}`;

    // Get country
    countries.registerLocale(
      require(`i18n-iso-countries/langs/en.json`)
    );
    const countryIso2Code = countries.getAlpha2Code(
      countryName,
      "en"
    );

    // Define the API endpoint and query parameters
    const params = {
      originCountryCode: "AE",
      originCityName: "Ajman",
      destinationCountryCode: countryIso2Code,
      destinationCityName: cityName,
      weight: totalWeight,
      length: length,
      width: width,
      height: height,
      plannedShippingDate: formattedPlannedShippingDate,
      isCustomsDeclarable: true,
      unitOfMeasurement: "metric",
    };

    if (postalCode) {
      params.destinationPostalCode = postalCode;
    }

    const cacheKey = getRateCacheKey(
      totalWeight,
      countryIso2Code,
      cityName,
      postalCode
    );

    const buildResult = (shippingProduct, shippingCost, shippingProductCode) => {
      const deliveryDate = new Date(
        shippingProduct?.deliveryCapabilities?.estimatedDeliveryDateAndTime
      );
      const currentDate = new Date();

      const timeDifference = deliveryDate - currentDate;
      const daysDifference = Math.ceil(
        timeDifference / (1000 * 60 * 60 * 24)
      );

      const minDays = daysDifference - 1;
      const maxDays = daysDifference + 1;

      const daysEstimate = `${Math.max(0, minDays)}-${Math.max(0, maxDays)}`;
      return [shippingCost, daysEstimate, shippingProductCode];
    };

    // Performs the actual lookup. Throws when the carrier returns an
    // unusable payload so the caller can retry or surface the failure.
    const requestRates = async () => {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}dhl-rates`,
        params,
          { timeout: 15000 }

      );

      const products = response?.data?.products;
      if (!Array.isArray(products)) {
        throw new Error("Invalid DHL rates response");
      }

      const exchangeRates = response.data.exchangeRates;
      let shippingProduct = null;
      for (const product of products) {
        if (product.productName == "EXPRESS WORLDWIDE") {
          shippingProduct = product;
        }
      }

      if (!shippingProduct) {
        throw new Error("No matching DHL product");
      }

      const shippingCost = await getProductPrice(shippingProduct, exchangeRates);
      return buildResult(
        shippingProduct,
        shippingCost,
        shippingProduct?.productCode || ""
      );
    };

    beginLoading();
    setError("");

    try {
      const cached = rateCache.get(cacheKey);
      if (cached && Date.now() - cached.time < RATE_CACHE_TTL_MS) {
        endLoading();
        return cached.value;
      }

      // Reuse the promise of an identical lookup already in flight rather
      // than issuing a second request for the same destination.
      if (inFlightRates.has(cacheKey)) {
        const result = await inFlightRates.get(cacheKey);
        endLoading();
        return result;
      }

      // The single silent retry lives inside the shared promise so every
      // caller waiting on this key shares the same final outcome.
      const pending = (async () => {
        try {
          const result = await requestRates().catch(() => requestRates());
          rateCache.set(cacheKey, { time: Date.now(), value: result });
          return result;
        } finally {
          inFlightRates.delete(cacheKey);
        }
      })();

      inFlightRates.set(cacheKey, pending);

      let result;
      try {
        result = await pending;
      } catch (err) {
        endLoading();
        setError("Failed to get shipping rates.");
        return [0, "", ""];
      }

      endLoading();
      return result;
    } catch (err) {
      endLoading();
      setError("Failed to get shipping rates.");
      return [0, "", ""];
    }
  };

  const getDHLPageRates = async (
    weight,
    senderCountryName,
    senderCityName,
    senderPostalCode,
    receiverCountryName,
    receiverCityName,
    receiverPostalCode
  ) => {
    const settings = await getDHLSettings();
    beginLoading();
    setError("");

    const totalWeight = parseFloat(weight);

    const length = settings?.find(
      (setting) => setting?.attributes?.SettingName == "DHL Length"
    )?.attributes?.SettingValue;

    const width = settings?.find(
      (setting) => setting?.attributes?.SettingName == "DHL Width"
    )?.attributes?.SettingValue;

    const height = settings?.find(
      (setting) => setting?.attributes?.SettingName == "DHL Height"
    )?.attributes?.SettingValue;

    const today = new Date();
    let plannedShippingDate = new Date(today);
    plannedShippingDate.setDate(today.getDate() + 1);

    if (plannedShippingDate.getDay() === 0) {
      plannedShippingDate.setDate(plannedShippingDate.getDate() + 1);
    }

    // Format the date as YYYY-MM-DD
    const formattedPlannedShippingDate = `${plannedShippingDate.getFullYear()}-${String(
      plannedShippingDate.getMonth() + 1
    ).padStart(2, "0")}-${String(plannedShippingDate.getDate()).padStart(
      2,
      "0"
    )}`;

    // Get country
    countries.registerLocale(
      require(`i18n-iso-countries/langs/en.json`)
    );
    const senderCountryIso2Code = countries.getAlpha2Code(
      senderCountryName,
      "en"
    );
    const receiverCountryIso2Code = countries.getAlpha2Code(
      receiverCountryName,
      "en"
    );

    // Define the API endpoint and query parameters
    const params = {
      originCountryCode: senderCountryIso2Code,
      originCityName: senderCityName,
      destinationCountryCode: receiverCountryIso2Code,
      destinationCityName: receiverCityName,
      weight: totalWeight,
      length: length,
      width: width,
      height: height,
      plannedShippingDate: formattedPlannedShippingDate,
      isCustomsDeclarable: true,
      unitOfMeasurement: "metric",
      tries: tries,
    };

    if (senderPostalCode) {
      params.originPostalCode = senderPostalCode;
    }
    if (receiverPostalCode) {
      params.destinationPostalCode = receiverPostalCode;
    }

    let shippingCost = 0.0;
    let shippingProduct = null;
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}dhl-rates`,
        params
      );
      const exchangeRates = response.data.exchangeRates;
      for (const product of response.data.products) {
        if (product.productName == "EXPRESS WORLDWIDE") {
          shippingProduct = product;
        }
      }

      // if (!shippingProduct) {
      //   let cheapestProduct = null;
      //   for (const product of response.data.products) {
      //     if (!cheapestProduct) {
      //       cheapestProduct = product;
      //     } else {
      //       if (
      //         getProductPrice(product, exchangeRates) <
      //         getProductPrice(cheapestProduct, exchangeRates)
      //       ) {
      //         cheapestProduct = product;
      //       }
      //     }
      //   }
      //   shippingProduct = cheapestProduct;
      // }

      if (shippingProduct) {
        shippingCost = await getProductPrice(shippingProduct, exchangeRates);
      }
    } catch (err) {
      setError("Failed to get shipping rates.");
      setTimeout(() => {
        setError("");
      }, 5000);
    }

    endLoading();
    let daysEstimate = "";
    const deliveryDate = new Date(
      shippingProduct?.deliveryCapabilities?.estimatedDeliveryDateAndTime
    );
    const currentDate = new Date();

    const timeDifference = deliveryDate - currentDate;
    const daysDifference = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));

    const minDays = daysDifference - 1;
    const maxDays = daysDifference + 1;

    daysEstimate = `${Math.max(0, minDays)}-${Math.max(0, maxDays)}`;
    return [shippingCost, daysEstimate];
  };

  const createShipment = async (
    countryName,
    cityName,
    postalCode,
    addressLine1,
    addressLine2,
    customerName,
    customerEmail,
    customerPhone,
    shippingProductCode,
    deliveryNotes,
    total,
    totalCurrency,
    description,
    lineItems,
    invoiceNum,
    pkgs,
    orderID
  ) => {
    const settings = await getDHLSettings();
    beginLoading();
    setError("");

    const accountNumber = settings?.find(
      (setting) => setting?.attributes?.SettingName == "DHL Account Number"
    )?.attributes?.SettingValue;

    const today = new Date();
    let plannedShippingDate = new Date(today);
    plannedShippingDate.setDate(today.getDate() + 1);

    if (plannedShippingDate.getDay() === 0) {
      plannedShippingDate.setDate(plannedShippingDate.getDate() + 1);
    }

    // Format the date as YYYY-MM-DD
    const dateWithoutTime = `${plannedShippingDate.getFullYear()}-${String(
      plannedShippingDate.getMonth() + 1
    ).padStart(2, "0")}-${String(plannedShippingDate.getDate()).padStart(
      2,
      "0"
    )}`;
    const formattedPlannedShippingDate = `${dateWithoutTime}T12:00:00 GMT+04:00`;

    // Get country
    countries.registerLocale(
      require(`i18n-iso-countries/langs/en.json`)
    );
    const countryIso2Code = countries.getAlpha2Code(
      countryName,
      "en"
    );

    // Define the API endpoint and query parameters
    const url = `${process.env.REACT_APP_API_URL}dhl-create-shipment`;
    const params = {
      plannedShippingDateAndTime: formattedPlannedShippingDate,
      productCode: shippingProductCode,
      pickup: {
        isRequested: false,
        specialInstructions: [
          {
            value: deliveryNotes,
            typeCode: "TBD",
          },
        ],
      },
      valueAddedServices: [
        {
          serviceCode: "WY",
        },
        {
          serviceCode: "DS",
        },
      ],
      outputImageProperties: {
        printerDPI: 300,
        encodingFormat: "pdf",
        imageOptions: [
          {
            typeCode: "invoice",
            templateName: "COMMERCIAL_INVOICE_P_10",
            isRequested: true,
            invoiceType: "commercial",
            languageCode: "eng",
            languageCountryCode: "US",
          },
          {
            typeCode: "waybillDoc",
            templateName: "ARCH_8x4",
            isRequested: true,
            hideAccountNumber: false,
            numberOfCopies: 1,
          },
          {
            typeCode: "label",
            templateName: "ECOM26_84CI_003",
            renderDHLLogo: true,
            fitLabelsToA4: false,
          },
        ],
      },
      accounts: [
        {
          number: accountNumber,
          typeCode: "shipper",
        },
      ],
      customerReferences: [
        {
          value: `ORD_${orderID}`,
          typeCode: "CO",
        },
      ],
      customerDetails: {
        shipperDetails: {
          postalAddress: {
            postalCode: "00000",
            cityName: "Ajman",
            countryCode: "AE",
            addressLine1: "Al JURF 3 Jeddah street",
          },
          contactInformation: {
            fullName: "Maryam",
            phone: "+971 56 525 5245",
            email: "classyym.dhl@gmail.com",
            companyName: "CLASSY Y Y M GARMENTS TRADING L.L.C",
          },
        },
        receiverDetails: {
          postalAddress: {
            postalCode: postalCode,
            cityName: cityName,
            countryCode: countryIso2Code,
            addressLine1: addressLine1,
          },
          contactInformation: {
            fullName: customerName,
            phone: customerPhone,
            email: customerEmail,
            companyName: customerName,
          },
        },
      },
      content: {
        packages: pkgs,
        exportDeclaration: {
          lineItems: lineItems,
          invoice: {
            number: invoiceNum,
            date: dateWithoutTime,
          },
          exportReason: "Personal, Not for Resale",
          exportReasonType: "personal_belongings_or_personal_use"
        },
        isCustomsDeclarable: true,
        declaredValue: total,
        declaredValueCurrency: totalCurrency,
        unitOfMeasurement: "metric",
        description: description,
        incoterm: "DAP",
      },
    };

    if (addressLine2?.trim() != "") {
      params.customerDetails.receiverDetails.postalAddress.addressLine2 = addressLine2;
    }

    let trackingNumber = "";
    let pdfDocument = "";
    let invoiceDocument = "";
    let shippingResponse = "";
    try {
      const response = await axios.post(url, params);
      trackingNumber = response.data.shipmentTrackingNumber;
      pdfDocument = response?.data?.documents?.find(
        (doc) => doc?.imageFormat == "PDF" && doc?.typeCode == "label"
      )?.content;
      invoiceDocument = response?.data?.documents?.find(
        (doc) => doc?.imageFormat == "PDF" && doc?.typeCode == "invoice"
      )?.content;
      // UPDATE 26 FEB, 2026
      shippingResponse = response?.data || "Success but no data";
      console.log("DHL Response: ", shippingResponse);
    } catch (err) {
      shippingResponse = err.response?.data || err.message || "Unknown Error";
      console.log("DHL Error: ", shippingResponse);
      setError("Failed to create shipment.");
      setTimeout(() => {
        setError("");
      }, 5000);
    }
    endLoading();
    return [trackingNumber, pdfDocument, invoiceDocument, shippingResponse];
  };

  return {
    getDHLSettings,
    getRates,
    getDHLPageRates,
    createShipment,
    loading,
    error,
    setError,
    retry,
  };
};

export default useDHLShipping;
